/**
 * Central list of all validation error and warning messages for the deal/order flow.
 * Use these constants so messages can be changed in one place.
 * Backend owns canonical text for API responses; this list is for proactive UI messages
 * and should align with backend wording where the same scenario applies.
 */
import { DealStatusEnum, LEGACY_NOT_PUBLISHED_STATUS } from '../deals.types';

/** Normalises a raw deal status for display: "Not Published" → "Expected". */
const toDisplayStatus = (raw: string): string =>
  raw.trim().toLowerCase() === LEGACY_NOT_PUBLISHED_STATUS.toLowerCase() ? DealStatusEnum.EXPECTED : raw.trim();

// ─── Errors (toast type: error) ─────────────────────────────────────────────
export const ERROR_MESSAGES = {
  ORDER_IN_TRANSIT: 'Order in transit - order cannot be changed',
  ORDER_CANCELLED: 'Order has been cancelled',
  ORDER_SUBMIT_FAILED: 'Order submission failed due to a technical issue. Please try again.',
  ORDER_CANCEL_FAILED: 'Order cancellation failed due to a technical issue. Please try again.',
} as const;

// ─── Warnings (toast type: warning) ─────────────────────────────────────────
export const WARNING_MESSAGES = {
  /** Shown when order is already re-confirmed: IS_CONFIRMED true, hasAnyPriceChangeFlag true, success status (ACCEPTED/SUBMITTED). Order cannot be edited. */
  ORDER_RECONFIRMED: 'Order has been reconfirmed and cannot be edited',
  /** Shown when in reprice window and order has no price-change flag (maturity not impacted by price change). */
  MATURITY_NOT_IMPACTED_BY_PRICE_CHANGE: 'Maturity not impacted by price change, order cannot be edited',
  /** Shown in Clear All modal when in reprice window and no orders can be reset (SUBMITTED_AND_DRAFT with price-change flag). */
  CLEAR_ALL_REPRICE_NO_RESET: 'Maturity not impacted by price change, order cannot be reset',
  /** Bulk header All: some rows skipped (fill-to-par was not enabled at last submit); others are saved. */
  BULK_APPLY_RECONFIRM_PAR_SKIPPED: 'Par amount cannot be increased for some orders.',
  /** Bulk header All: every modifiable row was ineligible for reconfirm par increase. */
  BULK_APPLY_RECONFIRM_PAR_NONE_ELIGIBLE: 'Par amount cannot be increased for all orders.',
} as const;

// ─── Reconfirm modal (single source of truth for standalone and in–Review & Submit overlay) ─
export const RECONFIRM_MODAL = {
  TITLE: 'Reconfirm Orders',
  BODY_1: 'Would you like to Reconfirm all orders for this deal?',
  BUTTON_CANCEL: 'Cancel',
  BUTTON_CONFIRM: 'Yes, Reconfirm All',
} as const;

export const RECONFIRM_REVIEW_SUBMIT_MODAL = {
  TITLE: 'Reconfirm Orders',
  BODY_1: 'Would you like to submit all modified orders and re-confirm unedited orders for this deal?',
  BUTTON_CANCEL: RECONFIRM_MODAL.BUTTON_CANCEL,
  BUTTON_CONFIRM: 'Yes, Submit',
} as const;

// ─── Dynamic message builders ───────────────────────────────────────────────

/** Reusable message when deal status blocks order changes. Aligns with backend ValidationMessages.ordersCannotBeChangedDealStatus (max 150 chars). */
export const getOrdersCannotBeChangedMessage = (dealStatus: string | null | undefined): string => {
  const status = typeof dealStatus === 'string' && dealStatus.trim() ? toDisplayStatus(dealStatus) : '—';
  return `Orders cannot be changed when deal status is: ${status}`;
};

/** Message when minimum is required because par is not divisible by 5 and multiples is null. UI-only; backend submit message is longer. */
export const MINIMUM_REQUIRED_WHEN_PAR_NOT_DIVISIBLE_BY_5_MESSAGE = 'Minimum is required';

/** Message for grid tooltip and Conditions modal when quantity exceeds submitted cap. Aligns with backend "Cannot exceed submitted amount". */
export const getReconfirmCapMessage = (submittedQuantity: string | number | null | undefined): string => {
  const s = String(submittedQuantity ?? '').trim();
  const n = parseFloat(s);
  const formatted = s === '' || isNaN(n) ? '—' : n.toLocaleString('en-US');
  return `Cannot exceed submitted amount: ${formatted}`;
};

/** Grid tooltip when a price-changed order cannot be included in Review and Submit during reconfirm. */
export const RECONFIRM_SUBMIT_EXCLUDED_TOOLTIP =
  'Excluded from Review and Submit during reconfirm — adjust order amount to include this maturity.';
